import { type TcsRegisterRow } from '@utazzo/shared';
import { UzBadge, UzButton, UzCard, UzDialog, UzInput, UzMultiSelect } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

import { FyRangeControls, useFyRange } from '../components/FyRangeControls';
import { TcsAddToChallanDialog } from '../components/TcsAddToChallanDialog';
import { TcsChallanFormDialog } from '../components/TcsChallanFormDialog';
import { TcsChallanPanel } from '../components/TcsChallanPanel';
import { TcsInvoiceCertificates } from '../components/TcsInvoiceCertificates';
import { TcsInvoicePaidDialog } from '../components/TcsInvoicePaidDialog';
import { getTcsRegister } from '../invoices.api';
import { formatInrPaise } from '../invoices.helpers';
import { useInvoiceAgents, useTcsRegister } from '../use-invoices';
import { useAddInvoices, useCreateChallan } from '../use-tcs-challans';
import { useMarkInvoicePaid } from '../use-tcs-invoices';

import { PageShell } from '@/components/page-shell';
import { dateInputToIso, formatDMY } from '@/lib/date-format';

/**
 * TCS register (route /reports/tcs-register), rendered to the client format: one
 * row per invoice that collected TCS, with the FY quarter/month, customer PAN,
 * the base TCS was charged on, the rate, and the TCS value. Paid status and the
 * TCS certificate are PER-INVOICE (mark paid / upload on the row), independent of
 * any challan; a challan only groups invoices (select rows -> Create challan /
 * Add to existing) and carries its challan document + audit timeline.
 * Filters: date range, agent, search (client / invoice / PAN / agent).
 */

const rupees = (paise: string): number => Number(paise) / 100;
const pctFraction = (rate: string): number => Number(rate) / 100;

const exportTcsToXlsx = (rows: TcsRegisterRow[]): void => {
  const data = rows.map((r, i) => ({
    'S.NO': i + 1,
    'CLIENT NAME': r.clientName ?? '',
    'INVOICE NO.': r.invoiceNumber,
    'PERIOD (QUARTER WISE)': r.quarter,
    MONTH: r.month,
    'PAN NO.': r.pan ?? '',
    'AMOUNT ON WHICH TCS DEDUCTED': rupees(r.baseAmountPaise),
    'RATE OF TCS': pctFraction(r.ratePercent),
    'TCS VALUE': rupees(r.tcsPaise),
    NATURE: r.nature,
    'TCS AMOUNT': rupees(r.tcsPaise),
    // Per-invoice paid status + certificate.
    'TCS PAID': r.tcsPaid ? 'Yes' : 'No',
    'PAYMENT DATE': r.tcsPaidAt ? formatDMY(r.tcsPaidAt) : '',
    CERTIFICATES: r.tcsCertificateCount,
    // Challan grouping (filled manually where needed).
    INTEREST: '',
    'CHALLAN VALUE': '',
    'CHALLAN SERIAL NO.': '',
    'BSR CODE': '',
    'CHALLAN NO': r.tcsChallanNo ?? '',
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'TCS Register');
  XLSX.writeFile(wb, `tcs-register-${new Date().toISOString().slice(0, 10)}.xlsx`);
};

type PaidFilter = 'ALL' | 'PAID' | 'UNPAID' | 'UNASSIGNED';

const FilterChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element => (
  <button
    type="button"
    onClick={onClick}
    className={
      active
        ? 'border-primary bg-primary text-primary-foreground rounded-full border px-3 py-1 text-xs font-medium'
        : 'rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-50'
    }
  >
    {label}
  </button>
);

export const TcsRegisterPage = (): React.JSX.Element => {
  const fyState = useFyRange();
  const { from, to } = fyState;
  const [agents, setAgents] = useState<string[]>([]);
  const [q, setQ] = useState('');
  const agentsQuery = useInvoiceAgents();

  const query = {
    from,
    to,
    ...(agents.length ? { travelAgentName: agents.join(',') } : {}),
    ...(q.trim() ? { q: q.trim() } : {}),
  };
  const registerQuery = useTcsRegister(query);
  const data = registerQuery.data;
  const allRows = data?.rows ?? [];
  const [paidFilter, setPaidFilter] = useState<PaidFilter>('ALL');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [panelChallanId, setPanelChallanId] = useState<string | null>(null);
  const [paidDialog, setPaidDialog] = useState<{ id: string; number: string } | null>(null);
  const [certInvoice, setCertInvoice] = useState<{ id: string; number: string } | null>(null);
  const createChallan = useCreateChallan();
  const addInvoices = useAddInvoices();
  const markPaid = useMarkInvoicePaid();
  const paidCount = allRows.filter((r) => r.tcsPaid).length;
  const unassignedCount = allRows.filter((r) => r.tcsChallanId === null).length;
  const unpaidCount = allRows.length - paidCount;
  const rows = allRows.filter((r) => {
    if (paidFilter === 'PAID') return r.tcsPaid;
    if (paidFilter === 'UNPAID') return !r.tcsPaid;
    if (paidFilter === 'UNASSIGNED') return r.tcsChallanId === null;
    return true;
  });
  const selectableRows = rows.filter((r) => r.tcsChallanId === null);
  const selectedIds = [...selected].filter((id) => allRows.some((r) => r.invoiceId === id));
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(r.invoiceId));
  const toggleOne = (id: string): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = (): void =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of selectableRows) {
        if (allSelected) next.delete(r.invoiceId);
        else next.add(r.invoiceId);
      }
      return next;
    });
  const totalBasePaise = rows.reduce((s, r) => s + BigInt(r.baseAmountPaise), 0n).toString();
  const totalTcsPaise = rows.reduce((s, r) => s + BigInt(r.tcsPaise), 0n).toString();

  const handleExport = async (): Promise<void> => {
    try {
      const full = await getTcsRegister(query as Parameters<typeof getTcsRegister>[0]);
      exportTcsToXlsx(full.rows);
    } catch {
      toast.error('Failed to export the register');
    }
  };

  return (
    <PageShell
      title="TCS register"
      description="Invoices that collected TCS for a period, with the quarter, PAN, base, rate and TCS value."
      actions={
        <UzButton
          variant="outline"
          leftIcon={<Download className="h-4 w-4" />}
          disabled={rows.length === 0}
          onClick={() => void handleExport()}
        >
          Export Excel
        </UzButton>
      }
    >
      <div className="mb-3">
        <UzInput
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search client / invoice / PAN / agent..."
          containerClassName="max-w-md"
        />
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <FyRangeControls state={fyState} />
        <div className="w-64">
          <UzMultiSelect
            label="Agents"
            placeholder="All agents"
            searchPlaceholder="Search agents..."
            values={agents}
            onValuesChange={setAgents}
            options={(agentsQuery.data ?? []).map((a) => ({ value: a, label: a }))}
          />
        </div>
        <UzButton variant="outline" onClick={() => void registerQuery.refetch()}>
          Refresh
        </UzButton>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <FilterChip
          label={`All (${allRows.length})`}
          active={paidFilter === 'ALL'}
          onClick={() => setPaidFilter('ALL')}
        />
        <FilterChip
          label={`Paid (${paidCount})`}
          active={paidFilter === 'PAID'}
          onClick={() => setPaidFilter('PAID')}
        />
        <FilterChip
          label={`Unpaid (${unpaidCount})`}
          active={paidFilter === 'UNPAID'}
          onClick={() => setPaidFilter('UNPAID')}
        />
        <FilterChip
          label={`No challan (${unassignedCount})`}
          active={paidFilter === 'UNASSIGNED'}
          onClick={() => setPaidFilter('UNASSIGNED')}
        />
      </div>

      {selectedIds.length > 0 && (
        <div
          role="region"
          aria-label="Selection actions"
          className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2"
        >
          <span className="text-sm font-medium text-zinc-800">{selectedIds.length} selected</span>
          <UzButton size="sm" onClick={() => setCreateOpen(true)}>
            Create challan
          </UzButton>
          <UzButton size="sm" variant="outline" onClick={() => setAddOpen(true)}>
            Add to existing challan
          </UzButton>
          <UzButton size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Clear
          </UzButton>
        </div>
      )}
      <p className="mb-2 text-xs text-zinc-500">
        Only invoices without a challan can be selected. To move an invoice, open its challan and
        remove it first.
      </p>

      <UzCard padding="none">
        {registerQuery.isLoading ? (
          <p className="p-6 text-sm text-zinc-500">Loading register...</p>
        ) : registerQuery.isError ? (
          <p className="p-6 text-sm text-red-600">Could not load the register.</p>
        ) : allRows.length === 0 ? (
          <p className="p-6 text-sm text-zinc-500">No TCS collected in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="border-b border-zinc-200 text-left text-xs font-medium text-zinc-500">
                  <th className="px-3 py-3">
                    <input
                      type="checkbox"
                      aria-label="Select all unassigned invoices"
                      checked={allSelected}
                      disabled={selectableRows.length === 0}
                      onChange={toggleAll}
                      className="h-4 w-4 cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-3">S.No</th>
                  <th className="px-3 py-3">Client</th>
                  <th className="px-3 py-3">Invoice No.</th>
                  <th className="px-3 py-3">Quarter</th>
                  <th className="px-3 py-3">Month</th>
                  <th className="px-3 py-3">PAN</th>
                  <th className="px-3 py-3 text-right">TCS Base</th>
                  <th className="px-3 py-3 text-right">Rate</th>
                  <th className="px-3 py-3 text-right">TCS Value</th>
                  <th className="px-3 py-3">Nature</th>
                  <th className="px-3 py-3">Challan</th>
                  <th className="px-3 py-3">TCS paid</th>
                  <th className="px-3 py-3">Certificate</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr
                    key={`${r.invoiceId}-${i}`}
                    className="border-b border-zinc-100 last:border-0"
                  >
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${r.invoiceNumber}`}
                        checked={selected.has(r.invoiceId)}
                        disabled={r.tcsChallanId !== null}
                        title={
                          r.tcsChallanId !== null
                            ? `Already in challan ${r.tcsChallanNo ?? ''}. Open it to remove first.`
                            : undefined
                        }
                        onChange={() => toggleOne(r.invoiceId)}
                        className="h-4 w-4 cursor-pointer disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="px-3 py-3 text-zinc-600 tabular-nums">{i + 1}</td>
                    <td className="px-3 py-3 text-zinc-700">{r.clientName ?? '-'}</td>
                    <td className="px-3 py-3 text-zinc-700 tabular-nums">{r.invoiceNumber}</td>
                    <td className="px-3 py-3 text-zinc-700">{r.quarter}</td>
                    <td className="px-3 py-3 text-zinc-700">{r.month}</td>
                    <td className="px-3 py-3 text-zinc-700 tabular-nums">{r.pan ?? '-'}</td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {formatInrPaise(r.baseAmountPaise)}
                    </td>
                    <td className="px-3 py-3 text-right text-zinc-700 tabular-nums">
                      {r.ratePercent}%
                    </td>
                    <td className="px-3 py-3 text-right font-medium text-zinc-900 tabular-nums">
                      {formatInrPaise(r.tcsPaise)}
                    </td>
                    <td className="px-3 py-3 text-zinc-700">{r.nature}</td>
                    <td className="px-3 py-3">
                      {r.tcsChallanId ? (
                        <button
                          type="button"
                          onClick={() => setPanelChallanId(r.tcsChallanId)}
                          aria-label={`Open challan for ${r.invoiceNumber}`}
                          className="text-primary text-sm underline-offset-2 hover:underline"
                        >
                          {r.tcsChallanNo ?? 'Open challan'}
                          {r.tcsChallanDate ? ` (${formatDMY(r.tcsChallanDate)})` : ''}
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <UzBadge tone={r.tcsPaid ? 'success' : 'warning'}>
                          {r.tcsPaid ? 'Paid' : 'Unpaid'}
                        </UzBadge>
                        {r.tcsPaid ? (
                          <UzButton
                            size="sm"
                            variant="ghost"
                            loading={markPaid.isPending}
                            onClick={() =>
                              markPaid.mutate(
                                { invoiceId: r.invoiceId, body: { paid: false } },
                                { onSuccess: () => toast.success('Marked unpaid') },
                              )
                            }
                          >
                            Mark unpaid
                          </UzButton>
                        ) : (
                          <UzButton
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setPaidDialog({ id: r.invoiceId, number: r.invoiceNumber })
                            }
                          >
                            Mark paid
                          </UzButton>
                        )}
                      </div>
                      {r.tcsPaid && r.tcsPaidAt ? (
                        <p className="mt-1 text-xs text-zinc-500">Paid {formatDMY(r.tcsPaidAt)}</p>
                      ) : null}
                    </td>
                    <td className="px-3 py-3">
                      <UzButton
                        size="sm"
                        variant="outline"
                        onClick={() => setCertInvoice({ id: r.invoiceId, number: r.invoiceNumber })}
                      >
                        {r.tcsCertificateCount > 0
                          ? `Certificate (${r.tcsCertificateCount})`
                          : 'Upload'}
                      </UzButton>
                    </td>
                  </tr>
                ))}
              </tbody>
              {data && (
                <tfoot>
                  <tr className="border-t border-zinc-200 bg-zinc-50 text-sm font-semibold text-zinc-900">
                    <td className="px-3 py-3" colSpan={7}>
                      {paidFilter === 'ALL' ? 'Totals' : 'Totals (filtered)'}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(totalBasePaise)}
                    </td>
                    <td className="px-3 py-3" />
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatInrPaise(totalTcsPaise)}
                    </td>
                    <td className="px-3 py-3" colSpan={4} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </UzCard>

      <TcsChallanFormDialog
        key={`create-${createOpen}`}
        open={createOpen}
        invoiceCount={selectedIds.length}
        pending={createChallan.isPending}
        onCancel={() => setCreateOpen(false)}
        onConfirm={({ challanNo, challanDate, note }) =>
          createChallan.mutate(
            {
              invoiceIds: selectedIds,
              ...(challanNo ? { challanNo } : {}),
              ...(challanDate ? { challanDate: dateInputToIso(challanDate) } : {}),
              ...(note ? { note } : {}),
            },
            {
              onSuccess: (c) => {
                setCreateOpen(false);
                setSelected(new Set());
                toast.success('Challan created');
                setPanelChallanId(c.id);
              },
            },
          )
        }
      />
      <TcsAddToChallanDialog
        key={`add-${addOpen}`}
        open={addOpen}
        invoiceCount={selectedIds.length}
        pending={addInvoices.isPending}
        onCancel={() => setAddOpen(false)}
        onConfirm={(challanId) =>
          addInvoices.mutate(
            { id: challanId, invoiceIds: selectedIds },
            {
              onSuccess: () => {
                setAddOpen(false);
                setSelected(new Set());
                toast.success('Invoices added to the challan');
              },
            },
          )
        }
      />
      <TcsChallanPanel challanId={panelChallanId} onClose={() => setPanelChallanId(null)} />

      <TcsInvoicePaidDialog
        key={`paid-${paidDialog?.id ?? 'none'}`}
        open={paidDialog !== null}
        subject={paidDialog ? `Invoice ${paidDialog.number}` : ''}
        pending={markPaid.isPending}
        onCancel={() => setPaidDialog(null)}
        onConfirm={({ paidAt }) => {
          if (!paidDialog) return;
          markPaid.mutate(
            { invoiceId: paidDialog.id, body: { paid: true, paidAt: dateInputToIso(paidAt) } },
            {
              onSuccess: () => {
                setPaidDialog(null);
                toast.success('Marked paid');
              },
            },
          );
        }}
      />

      <UzDialog
        open={certInvoice !== null}
        onOpenChange={(o) => !o && setCertInvoice(null)}
        title="TCS certificate"
        description={certInvoice ? `Invoice ${certInvoice.number}` : ''}
        size="lg"
      >
        {certInvoice ? <TcsInvoiceCertificates invoiceId={certInvoice.id} /> : null}
      </UzDialog>
    </PageShell>
  );
};
